import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import Breadcrumbs from '../../../components/breadcrumbs'
import Footer from '../../../components/footer'
import { getCanonicalUrl } from '../../../utils/canonical'
import DarkNav from '../../../components/dark-nav'
import './zapier-feature.css'

const SMSDeliveryStatus = () => {
  const [showScrollTop, setShowScrollTop] = useState(false)

  useEffect(() => {
    if (typeof AOS !== 'undefined') {
      AOS.init({
        duration: 800,
        easing: 'ease-out',
        once: false,
        offset: 100,
        delay: 0,
        mirror: true
      })
    }

    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300)
      if (typeof AOS !== 'undefined') {
        AOS.refresh()
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Integrations' },
    { label: 'Zapier', path: '/integrations/zapier' },
    { label: 'SMS Delivery Status Changed' }
  ]

  const useCases = [
    {
      title: "Automated Failed Delivery Alert System",
      industry: "Customer Support & Operations",
      description: "Immediately notify your team when critical SMS messages fail to deliver, enabling rapid customer follow-up through alternative channels.",
      trigger: "DataFlows SMS - SMS Delivery Status Changed",
      steps: [
        "Send appointment reminder SMS to customer: 'Your appointment is tomorrow at 2pm'",
        "Mobile carrier returns 'Failed' status (invalid number or network issue)",
        "Zapier trigger fires with delivery status: 'failed'",
        "Filter: Only continue if status = 'failed' AND message type = 'appointment'",
        "Create urgent task in Asana: 'Contact customer re: failed appointment reminder'",
        "Send Slack notification to @customer-support: '⚠️ Failed SMS to customer ID 12345'",
        "Log failure to Google Sheets for compliance and tracking",
        "Automatically send email to customer as backup communication channel"
      ],
      keywords: "SMS delivery tracking Australia, failed SMS alerts, delivery confirmation SMS, SMS monitoring Australia"
    },
    {
      title: "CRM Delivery Status Synchronisation",
      industry: "Sales & Marketing",
      description: "Automatically update your CRM contact records with SMS delivery confirmations, ensuring accurate communication history for Australian businesses.",
      trigger: "DataFlows SMS - SMS Delivery Status Changed",
      steps: [
        "Sales team sends promotional SMS to 5,000 Australian contacts",
        "Delivery status updates arrive: 4,847 Delivered, 98 Failed, 55 Bounced",
        "For each status update, lookup contact in HubSpot by mobile number",
        "Update contact property: 'Last SMS Status' = Delivered/Failed/Bounced",
        "Update contact property: 'Last SMS Delivery Date' = timestamp",
        "Create timeline event in HubSpot: 'SMS Delivered Successfully'",
        "For failed deliveries: Add contact to 'Invalid Numbers' list",
        "For bounced deliveries: Flag for mobile number verification",
        "Generate weekly CRM report showing delivery success rate by campaign"
      ],
      keywords: "CRM SMS integration Australia, HubSpot SMS delivery, contact management SMS, delivery tracking CRM"
    },
    {
      title: "Compliance and Audit Trail Management",
      industry: "Healthcare & Financial Services",
      description: "Maintain comprehensive delivery records for regulatory compliance, with automated logging of every SMS delivery status change for Australian compliance standards.",
      trigger: "DataFlows SMS - SMS Delivery Status Changed",
      steps: [
        "Healthcare provider sends appointment confirmation to patient",
        "Delivery status changes from 'Sent' to 'Delivered'",
        "Log complete delivery data to dedicated Google Sheets: Date, Time, Patient ID, Status, Message ID",
        "Create backup entry in Airtable with status timestamp and delivery receipt",
        "For medical appointment reminders: Update patient management system with delivery confirmation",
        "Store delivery receipt in secure document management system (DocuSign/Box)",
        "Generate monthly compliance report for audit purposes",
        "Retain records for 7 years per Australian healthcare regulations",
        "Alert compliance officer if delivery rate drops below 95%"
      ],
      keywords: "SMS compliance Australia, delivery audit trail, healthcare SMS tracking, regulatory SMS reporting"
    },
    {
      title: "Intelligent SMS Retry Logic",
      industry: "E-commerce & Retail",
      description: "Automatically retry failed SMS deliveries using smart logic, optimising delivery rates for time-sensitive Australian retail communications.",
      trigger: "DataFlows SMS - SMS Delivery Status Changed",
      steps: [
        "Order dispatch notification SMS sent to customer",
        "Status changes to 'Bounced' (temporary network failure)",
        "Filter: Only retry if status = 'bounced' AND retry count < 3",
        "Wait 15 minutes (gives network time to recover)",
        "Resend SMS via DataFlows with retry flag",
        "If second attempt fails: Wait 1 hour and try alternative message format",
        "If third attempt fails: Switch to email notification instead",
        "Log all retry attempts to Google Sheets for analysis",
        "Update customer preference: 'Prefers email' if SMS consistently fails"
      ],
      keywords: "SMS retry logic Australia, delivery optimisation, SMS fallback strategy, automated SMS resend"
    },
    {
      title: "Real-Time Delivery Rate Dashboard",
      industry: "Marketing & Analytics",
      description: "Monitor SMS campaign performance in real-time by tracking delivery, bounce, and failure rates across Australian mobile networks.",
      trigger: "DataFlows SMS - SMS Delivery Status Changed",
      steps: [
        "Campaign sends 10,000 SMS messages to Australian mobile numbers",
        "Each delivery status update triggers workflow",
        "Increment counters in Google Sheets: Total Sent, Delivered, Failed, Bounced",
        "Calculate delivery rate: (Delivered / Total Sent) × 100",
        "Update live dashboard in Google Data Studio",
        "Track delivery rates by carrier: Telstra, Optus, Vodafone, MVNOs",
        "Identify problematic number ranges or patterns",
        "Alert marketing team if delivery rate drops below 92%",
        "Generate insights: 'Vodafone numbers have 3% higher bounce rate'",
        "Optimise future campaigns based on carrier performance data"
      ],
      keywords: "SMS delivery rate Australia, campaign tracking SMS, SMS analytics dashboard, mobile carrier tracking"
    },
    {
      title: "Customer Engagement Quality Scoring",
      industry: "Customer Success",
      description: "Track SMS delivery success rates per customer to identify communication issues and maintain high-quality contact data for Australian customer bases.",
      trigger: "DataFlows SMS - SMS Delivery Status Changed",
      steps: [
        "System sends weekly engagement SMS to 2,500 customers",
        "Delivery statuses flow in for each customer",
        "Lookup customer in database by mobile number",
        "Update customer engagement score based on delivery success",
        "Delivered = +5 points | Failed = -10 points | Bounced = -5 points",
        "Customers with score < 50: Flag for mobile number update",
        "Customers with 3+ consecutive failures: Mark as 'Invalid Contact'",
        "Send personalised email: 'Update your mobile number for better service'",
        "Create customer success task to verify contact details",
        "Generate monthly report: 'Top 100 customers with contact issues'"
      ],
      keywords: "customer data quality Australia, contact verification SMS, engagement tracking Australia, customer database management"
    },
    {
      title: "Multi-Channel Communication Failover",
      industry: "Emergency Services & Critical Communications",
      description: "Ensure critical messages reach recipients by automatically switching to alternative channels when SMS delivery fails, crucial for Australian emergency communications.",
      trigger: "DataFlows SMS - SMS Delivery Status Changed",
      steps: [
        "Security company sends emergency alert SMS: 'Alarm triggered at property'",
        "Status updates to 'Failed' (number unreachable)",
        "Immediately trigger backup communication workflow",
        "Attempt 1: Send voice call via Twilio to mobile number",
        "Attempt 2: Send email to registered email address",
        "Attempt 3: Send push notification via mobile app",
        "Attempt 4: SMS to secondary contact number on file",
        "Log all communication attempts with timestamps",
        "Create urgent ticket in support system for follow-up",
        "Notify account manager: 'Critical message failed - customer ID 789'"
      ],
      keywords: "SMS failover Australia, critical communications backup, multi-channel messaging, emergency alert delivery"
    }
  ]

  const faqItems = [
    {
      question: "What delivery statuses can DataFlows SMS track in Australia?",
      answer: "DataFlows SMS tracks three primary delivery statuses: 'Delivered' (message successfully received by recipient's phone), 'Failed' (permanent delivery failure due to invalid number, blocked service, or carrier rejection), and 'Bounced' (temporary failure due to phone off, out of range, or network congestion). Australian mobile carriers provide detailed delivery receipts that enable precise status tracking for Telstra, Optus, Vodafone, and MVNO networks."
    },
    {
      question: "How quickly do delivery status updates trigger my Zapier workflow?",
      answer: "Delivery status updates trigger instantly via webhooks, typically within 1-3 seconds of the status change. Unlike polling-based integrations that check every 5-15 minutes, DataFlows SMS uses real-time webhook triggers, meaning your Zapier workflow fires immediately when a delivery status changes. This is crucial for time-sensitive workflows like failed delivery alerts or compliance logging."
    },
    {
      question: "Can I track delivery rates for different Australian mobile carriers?",
      answer: "Yes! The delivery status data includes carrier information, allowing you to track performance across Telstra, Optus, Vodafone, and various MVNOs. You can build workflows that analyse delivery rates by carrier, identify network-specific issues, and optimise your SMS strategy based on which carriers your customers use. This is particularly valuable for Australian businesses managing large-scale SMS campaigns."
    },
    {
      question: "What happens if an SMS is marked as 'Bounced' versus 'Failed'?",
      answer: "A 'Bounced' status indicates a temporary delivery issue (phone off, out of coverage, or network congestion) that may succeed if retried later. Australian best practice is to retry bounced messages after 15-60 minutes. A 'Failed' status indicates a permanent issue (invalid number, service blocked, or carrier rejection) that won't succeed on retry. Your Zapier workflow should handle these differently: retry bounced messages, but mark failed numbers for review or removal."
    },
    {
      question: "Can I use delivery status tracking for compliance and audit requirements?",
      answer: "Absolutely! Delivery status tracking is essential for Australian compliance in healthcare (RACGP standards), financial services (ASIC requirements), and other regulated industries. You can automatically log every delivery confirmation with timestamps, message IDs, and recipient details to Google Sheets, Airtable, or secure document storage. This creates a comprehensive audit trail proving message delivery for legal and regulatory purposes."
    },
    {
      question: "How do I reduce failed SMS deliveries for my Australian customer base?",
      answer: "Use DataFlows delivery status tracking to identify patterns: Are failures clustered in specific area codes? Is one carrier performing poorly? Are numbers formatted incorrectly? Build Zapier workflows that automatically flag invalid numbers, prompt customers to update contact details, and maintain a database of verified mobile numbers. Australian businesses typically see 2-5% failure rates; anything higher indicates data quality issues worth investigating."
    },
    {
      question: "Can I automatically switch to email if SMS delivery fails?",
      answer: "Yes! This is a popular Australian use case. When your Zapier workflow receives a 'Failed' delivery status, add a filter to check the status, then trigger an email send action (Gmail, SendGrid, Mailchimp, etc.) as a backup. For critical communications like appointment reminders or security alerts, multi-channel failover ensures your message reaches the recipient even if their mobile number is invalid or unreachable."
    },
    {
      question: "What delivery rate should I expect for SMS campaigns in Australia?",
      answer: "Well-maintained Australian contact databases typically achieve 95-98% delivery rates. Rates below 92% suggest data quality issues (outdated numbers, incorrect formatting, or numbers that have been disconnected). Use DataFlows delivery status tracking to monitor your baseline rate, investigate sudden drops, and maintain list hygiene by removing failed numbers. Different message types may have varying rates: transactional SMS typically deliver better than marketing messages."
    },
    {
      question: "Can I track how long it takes for SMS messages to be delivered?",
      answer: "Yes! The delivery status update includes timestamps for when the message was sent and when delivery was confirmed. Calculate delivery time by comparing these timestamps in your Zapier workflow. For Australian SMS, typical delivery times are 1-5 seconds for domestic numbers on major carriers. Monitoring delivery times helps identify network congestion, carrier performance issues, or problems with specific number ranges."
    },
    {
      question: "How does delivery status tracking integrate with CRM systems?",
      answer: "DataFlows SMS delivery status updates work seamlessly with popular CRMs via Zapier. When a status changes, look up the contact in HubSpot, Salesforce, Pipedrive, or Zoho CRM by mobile number, then update custom fields with delivery status, timestamps, and delivery rate scores. This enriches customer profiles with communication history and helps sales/support teams understand which customers have reliable contact information. Australian businesses use this for data quality scoring and customer engagement tracking."
    }
  ]

  return (
    <div className="zapier-feature-container">
      <DarkNav />
      <Helmet>
        <title>SMS Delivery Status Changed - DataFlows Zapier | SMS Delivery Tracking Australia</title>
        <meta
          name="description"
          content="Track SMS delivery status changes in real-time for Australian businesses. Automate alerts for failed deliveries, update CRM with delivery confirmations, monitor delivery rates, and ensure compliance. Instant webhooks, comprehensive tracking."
        />
        <meta name="keywords" content="SMS delivery tracking Australia, SMS status webhook Australia, delivery confirmation SMS, failed SMS alerts, SMS delivery rate monitoring, SMS compliance Australia, delivery receipt automation, Australian SMS tracking, mobile carrier delivery tracking" />
        <meta property="og:title" content="SMS Delivery Status Tracking for Australian Businesses - DataFlows Zapier" />
        <meta property="og:description" content="Automatically track when SMS delivery status changes. Alert teams on failures, update CRM with confirmations, monitor delivery rates, implement retry logic, maintain compliance." />
        <link rel="canonical" href={getCanonicalUrl('/integrations/zapier/triggers/sms-delivery-status')} />
      </Helmet>

      <div className="feature-breadcrumbs">
        <div className="container">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
      </div>

      <section className="feature-hero" data-aos="fade-up">
        <div className="container">
          <div className="hero-badge">Real-Time Trigger</div>
          <h1>SMS Delivery Status Changed</h1>
          <p className="hero-subtitle">
            Automatically track when SMS delivery status changes from sent to delivered, failed, or bounced. Alert your team on failed deliveries, update CRM records with delivery confirmations, monitor campaign delivery rates, implement intelligent retry logic, and maintain comprehensive compliance records for Australian businesses.
          </p>
          <div className="hero-stats">
            <div className="stat-item">
              <div className="stat-value">1-3s</div>
              <div className="stat-label">Webhook Speed</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">3</div>
              <div className="stat-label">Status Types</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">100%</div>
              <div className="stat-label">Coverage Rate</div>
            </div>
          </div>
        </div>
      </section>

      <section className="feature-overview" data-aos="fade-up">
        <div className="container">
          <h2>What Is SMS Delivery Status Tracking?</h2>
          <p className="section-intro">
            SMS delivery status tracking monitors the journey of every text message you send, providing real-time updates when delivery status changes. Unlike basic SMS sending where you're left wondering if messages arrived, delivery status tracking gives Australian businesses definitive confirmation of whether each message was successfully delivered, permanently failed, or temporarily bounced. This visibility is crucial for customer communications, compliance requirements, and maintaining high-quality contact databases.
          </p>

          <div className="overview-grid">
            <div className="overview-card">
              <div className="card-icon">✅</div>
              <h3>Delivered Status</h3>
              <p>Message successfully reached the recipient's mobile phone and was accepted by their device. This is the ideal outcome confirming your communication succeeded.</p>
              <div className="emotion-tags">
                <span>Success Confirmed</span>
                <span>Customer Reached</span>
                <span>Communication Complete</span>
                <span>Audit Trail</span>
              </div>
            </div>

            <div className="overview-card">
              <div className="card-icon">❌</div>
              <h3>Failed Status</h3>
              <p>Permanent delivery failure due to invalid number, disconnected service, number ported, or carrier rejection. Requires manual intervention or alternative contact method.</p>
              <div className="emotion-tags negative">
                <span>Invalid Number</span>
                <span>Service Blocked</span>
                <span>Disconnected</span>
                <span>Requires Action</span>
              </div>
            </div>

            <div className="overview-card">
              <div className="card-icon">⚠️</div>
              <h3>Bounced Status</h3>
              <p>Temporary delivery failure due to phone turned off, out of coverage range, or network congestion. Message may succeed if retried after delay.</p>
              <div className="emotion-tags neutral">
                <span>Temporary Issue</span>
                <span>Retry Later</span>
                <span>Network Problem</span>
                <span>Recoverable</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="how-it-works" data-aos="fade-up">
        <div className="container">
          <h2>How Delivery Status Tracking Works in Zapier</h2>
          <p className="section-intro">
            DataFlows SMS integrates with Zapier to provide instant, real-time delivery status updates for every SMS sent through your Australian business number. Unlike polling-based systems that check for updates every 5-15 minutes, DataFlows uses webhook triggers that fire within 1-3 seconds of a status change.
          </p>

          <div className="workflow-steps">
            <div className="workflow-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Send SMS Message</h3>
                <p>Your business sends an SMS via DataFlows (manually, API, or another Zapier workflow). The message is queued for delivery to the Australian mobile network.</p>
                <div className="code-example">
                  To: +61412345678<br/>
                  From: +61400123456<br/>
                  Message: "Your package has been dispatched!"<br/>
                  Message ID: msg_abc123xyz
                </div>
              </div>
            </div>

            <div className="workflow-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Carrier Processes Delivery</h3>
                <p>The message travels through Australian mobile networks (Telstra, Optus, Vodafone) to the recipient's device. The carrier monitors delivery progress.</p>
              </div>
            </div>

            <div className="workflow-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Status Changes and Webhook Fires</h3>
                <p>When the carrier confirms delivery (or failure), DataFlows receives the delivery receipt and instantly triggers your Zapier workflow via webhook.</p>
                <div className="code-example">
                  message_id: "msg_abc123xyz"<br/>
                  status: "delivered"<br/>
                  to: "+61412345678"<br/>
                  delivered_at: "2025-11-10T14:32:15+10:00"<br/>
                  carrier: "Telstra"
                </div>
              </div>
            </div>

            <div className="workflow-step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>Take Automated Action</h3>
                <p>Your Zapier workflow receives the status update and takes appropriate action based on the result.</p>
                <ul>
                  <li>Delivered → Update CRM, log success, calculate metrics</li>
                  <li>Failed → Alert team, flag for review, try alternative channel</li>
                  <li>Bounced → Queue for retry, wait and resend</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="use-cases-section" data-aos="fade-up">
        <div className="container">
          <h2>Real-World Use Cases for Australian Businesses</h2>
          <p className="section-intro">
            Discover how Australian businesses across industries use SMS delivery status tracking to improve communication reliability, maintain compliance, enhance customer experience, and optimise contact data quality. These detailed workflows demonstrate practical applications from healthcare to e-commerce.
          </p>

          {useCases.map((useCase, index) => (
            <div key={index} className="use-case-detailed" data-aos="fade-up">
              <div className="use-case-header">
                <div>
                  <h3>{useCase.title}</h3>
                  <span className="industry-tag">{useCase.industry}</span>
                </div>
              </div>
              <p className="use-case-description">{useCase.description}</p>

              <div className="workflow-breakdown">
                <h4>Workflow Steps:</h4>
                <ol>
                  {useCase.steps.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
              </div>

            </div>
          ))}
        </div>
      </section>

      <section className="australian-focus" data-aos="fade-up">
        <div className="container">
          <h2>Why Australian Businesses Choose DataFlows SMS Delivery Tracking</h2>
          <div className="benefits-grid">
            <div className="benefit-item">
              <h3>🇦🇺 Australian Mobile Network Support</h3>
              <p>Full delivery receipt support for all Australian carriers: Telstra, Optus, Vodafone, and MVNOs. Track carrier-specific delivery performance and identify network issues.</p>
            </div>
            <div className="benefit-item">
              <h3>⚡ Instant Webhook Triggers</h3>
              <p>Real-time delivery status updates with 1-3 second webhook firing. No polling delays means your workflows respond immediately to delivery failures or successes.</p>
            </div>
            <div className="benefit-item">
              <h3>📊 Comprehensive Status Data</h3>
              <p>Detailed delivery information including status type, timestamps, carrier details, error codes, and delivery receipts for full audit trails.</p>
            </div>
            <div className="benefit-item">
              <h3>🔒 Australian Compliance Ready</h3>
              <p>Meet regulatory requirements for healthcare (RACGP), financial services (ASIC), and other industries requiring proof of message delivery and comprehensive audit logs.</p>
            </div>
            <div className="benefit-item">
              <h3>💰 Included in Your Plan</h3>
              <p>Delivery status tracking is included with all DataFlows SMS plans. No additional per-message charges for delivery receipts or status webhooks.</p>
            </div>
            <div className="benefit-item">
              <h3>🔗 6,000+ Zapier Integrations</h3>
              <p>Connect delivery status tracking to any CRM, support platform, spreadsheet, or business tool. Update HubSpot, create Slack alerts, log to Google Sheets, and more.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="setup-guide" data-aos="fade-up">
        <div className="container">
          <h2>How to Set Up SMS Delivery Status Tracking in Zapier</h2>

          <div className="setup-step">
            <h3>Step 1: Create Your Zap</h3>
            <ol>
              <li>Log in to <a href="https://zapier.com" target="_blank" rel="noopener noreferrer">Zapier</a></li>
              <li>Click "Create Zap"</li>
              <li>Search for "DataFlows SMS" and select it as your trigger app</li>
            </ol>
          </div>

          <div className="setup-step">
            <h3>Step 2: Choose Delivery Status Trigger</h3>
            <ol>
              <li>Select trigger event: <strong>"SMS Delivery Status Changed"</strong></li>
              <li>Connect your DataFlows SMS account using your API token</li>
              <li>Get your API token from: <a href="https://sms.dataflows.com.au/developers/api" target="_blank" rel="noopener noreferrer">https://sms.dataflows.com.au/developers/api</a></li>
              <li>Test the trigger by sending a test SMS and waiting for delivery confirmation</li>
            </ol>
          </div>

          <div className="setup-step">
            <h3>Step 3: Filter by Delivery Status</h3>
            <ol>
              <li>Add a <strong>Filter</strong> step to your workflow</li>
              <li>Choose what status changes to act on:
                <ul>
                  <li><strong>Failed deliveries only:</strong> Status equals "failed"</li>
                  <li><strong>Successful deliveries:</strong> Status equals "delivered"</li>
                  <li><strong>Bounced messages:</strong> Status equals "bounced"</li>
                  <li><strong>All non-delivered:</strong> Status does not equal "delivered"</li>
                </ul>
              </li>
              <li>You can also filter by message type, recipient, or other fields</li>
            </ol>
          </div>

          <div className="setup-step">
            <h3>Step 4: Add Your Action Steps</h3>
            <ol>
              <li>Choose what happens when delivery status changes</li>
              <li><strong>For failed deliveries:</strong>
                <ul>
                  <li>Send Slack notification to support team</li>
                  <li>Create task in project management tool (Asana, Trello, Monday)</li>
                  <li>Update CRM record (HubSpot, Salesforce, Pipedrive)</li>
                  <li>Send fallback email to customer</li>
                  <li>Log to Google Sheets for analysis</li>
                </ul>
              </li>
              <li><strong>For successful deliveries:</strong>
                <ul>
                  <li>Update CRM with delivery confirmation timestamp</li>
                  <li>Log to compliance spreadsheet</li>
                  <li>Increment success counter in analytics</li>
                  <li>Mark customer engagement task as complete</li>
                </ul>
              </li>
              <li><strong>For bounced messages:</strong>
                <ul>
                  <li>Wait 15-60 minutes using Zapier Delay</li>
                  <li>Retry sending via DataFlows SMS "Send SMS" action</li>
                  <li>Track retry attempts in database</li>
                </ul>
              </li>
            </ol>
          </div>

          <div className="setup-step">
            <h3>Step 5: Test and Monitor</h3>
            <ol>
              <li>Send test SMS messages to your own Australian mobile number</li>
              <li>Verify delivery status webhook fires and workflow executes</li>
              <li>Test failed delivery by sending to an invalid number (e.g., +61400000000)</li>
              <li>Check that filters work correctly for each status type</li>
              <li>Monitor Zap history for the first 24 hours to ensure reliability</li>
              <li>Turn on your Zap to start tracking all SMS delivery statuses!</li>
            </ol>
          </div>

          <div className="setup-step">
            <h3>Advanced: Multi-Path Workflows</h3>
            <ol>
              <li>For complex workflows, use Zapier <strong>Paths</strong> instead of simple filters</li>
              <li>Create separate paths for each status type with different actions:
                <ul>
                  <li><strong>Path A (Delivered):</strong> Update CRM + Log success metrics</li>
                  <li><strong>Path B (Failed):</strong> Alert team + Try email fallback</li>
                  <li><strong>Path C (Bounced):</strong> Queue for retry + Track attempts</li>
                </ul>
              </li>
              <li>Add sub-filters within paths for more granular control</li>
              <li>Example: Within "Failed" path, create sub-paths for different error codes</li>
            </ol>
          </div>
        </div>
      </section>

      <section className="faq-section" data-aos="fade-up">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            {faqItems.map((item, index) => (
              <div key={index} className="faq-item">
                <h3 className="faq-question">{item.question}</h3>
                <p className="faq-answer">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="technical-details" data-aos="fade-up">
        <div className="container">
          <h2>Technical Details for Australian Developers</h2>

          <div className="tech-section">
            <h3>Webhook Payload Structure</h3>
            <p>When delivery status changes, DataFlows sends a webhook to Zapier with the following data structure:</p>
            <div className="code-example">
              {`{
  "message_id": "msg_abc123xyz",
  "status": "delivered",
  "to": "+61412345678",
  "from": "+61400123456",
  "sent_at": "2025-11-10T14:30:00+10:00",
  "delivered_at": "2025-11-10T14:30:03+10:00",
  "carrier": "Telstra",
  "message_content": "Your package has been dispatched!",
  "segments": 1,
  "error_code": null,
  "error_message": null,
  "metadata": {
    "campaign_id": "campaign_123",
    "customer_id": "cust_789"
  }
}`}
            </div>
          </div>

          <div className="tech-section">
            <h3>Status Types and Error Codes</h3>
            <p>Understanding Australian carrier-specific status codes:</p>
            <ul>
              <li><strong>delivered:</strong> Successfully delivered to recipient's device</li>
              <li><strong>failed:</strong> Permanent failure (error codes: INVALID_NUMBER, SERVICE_BLOCKED, CARRIER_REJECTED)</li>
              <li><strong>bounced:</strong> Temporary failure (error codes: PHONE_OFF, OUT_OF_COVERAGE, NETWORK_CONGESTION)</li>
              <li><strong>expired:</strong> Message expired before delivery (48-hour validity period exceeded)</li>
            </ul>
          </div>

          <div className="tech-section">
            <h3>Australian Mobile Number Format</h3>
            <p>DataFlows automatically handles Australian number formatting:</p>
            <ul>
              <li>Input: 0412 345 678 → Output: +61412345678 (E.164 format)</li>
              <li>Input: 61412345678 → Output: +61412345678</li>
              <li>Input: +61 4 1234 5678 → Output: +61412345678</li>
            </ul>
            <p>Delivery receipts always return numbers in E.164 format (+61...) for consistency.</p>
          </div>

          <div className="tech-section">
            <h3>Carrier-Specific Behaviour</h3>
            <ul>
              <li><strong>Telstra:</strong> Fastest delivery receipts (1-2 seconds), most reliable</li>
              <li><strong>Optus:</strong> Delivery receipts within 2-5 seconds</li>
              <li><strong>Vodafone:</strong> Delivery receipts within 3-8 seconds</li>
              <li><strong>MVNOs:</strong> Delivery receipt timing depends on parent network (Telstra/Optus)</li>
            </ul>
          </div>

          <div className="tech-section">
            <h3>Best Practices for Australian SMS Delivery</h3>
            <ol>
              <li><strong>Always validate numbers:</strong> Use Australian mobile regex: ^(\+?61|0)4\d{8}$</li>
              <li><strong>Handle bounces gracefully:</strong> Retry after 15-60 minutes, maximum 3 attempts</li>
              <li><strong>Monitor delivery rates:</strong> Baseline 95-98% for good data, investigate if below 92%</li>
              <li><strong>Respect quiet hours:</strong> No SMS 9pm-8am AEST/AEDT per ACMA guidelines</li>
              <li><strong>Maintain audit logs:</strong> Store delivery confirmations for 7 years (compliance)</li>
              <li><strong>Use message metadata:</strong> Track campaign IDs, customer IDs for analysis</li>
              <li><strong>Implement fallback:</strong> Email or voice call if SMS fails critically</li>
            </ol>
          </div>
        </div>
      </section>

      <section className="related-features" data-aos="fade-up">
        <div className="container">
          <h2>Related DataFlows SMS Features</h2>
          <div className="related-grid">
            <Link to="/integrations/zapier/triggers/new-sms-received" className="related-card">
              <h3>📥 New SMS Received</h3>
              <p>Trigger workflows when customers reply to your SMS messages</p>
            </Link>
            <Link to="/integrations/zapier/actions/send-sms" className="related-card">
              <h3>📤 Send SMS</h3>
              <p>Send SMS messages to Australian mobile numbers from any Zapier workflow</p>
            </Link>
            <Link to="/integrations/zapier/actions/create-contact" className="related-card">
              <h3>📇 Create Contact</h3>
              <p>Automatically add contacts to DataFlows from any Zapier trigger</p>
            </Link>
          </div>
        </div>
      </section>

      <section className="cta-section" data-aos="fade-up">
        <div className="container">
          <h2>Ready to Track SMS Delivery Status?</h2>
          <p>Connect DataFlows SMS with Zapier and start monitoring delivery confirmations, alerting on failures, and maintaining comprehensive audit trails for your Australian business communications.</p>
          <div className="cta-buttons">
            <a href="https://zapier.com/apps/dataflows-sms" className="btn btn-primary" target="_blank" rel="noopener noreferrer">
              Get Started with Zapier
            </a>
            <Link to="/integrations/zapier" className="btn btn-secondary">
              View All Zapier Features
            </Link>
          </div>
        </div>
      </section>

      {showScrollTop && (
        <button className="scroll-to-top" onClick={scrollToTop} aria-label="Scroll to top">
          ↑
        </button>
      )}

      <Footer />
    </div>
  )
}

export default SMSDeliveryStatus
